import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Text from '../ui/Text';
import { colors, hairline, radii, spacing } from '../../theme';
import { Service } from '../../data/services';
import { hourChoices, hourRange } from '../../data/hourly';

/**
 * How long a Pro is being booked for, on a service sold by the hour.
 *
 * ---------------------------------------------------------------------------
 * A ROW OF CHOICES RATHER THAN A STEPPER
 * ---------------------------------------------------------------------------
 * The basket already has a stepper, and it means quantity - two of a thing.
 * Hours are not a second quantity: four hours of one Pro is one visit, and two
 * visits of two hours is something else entirely. Two identical controls a
 * centimetre apart meaning different things is how somebody books eight hours
 * of cleaning by accident.
 *
 * The range is one to four, so every option fits on a row and the whole choice
 * is visible at once - which is also why there is no "how many hours?" empty
 * state to resolve: one of them is always selected.
 *
 * ---------------------------------------------------------------------------
 * THE TOTAL IS PART OF THE CONTROL
 * ---------------------------------------------------------------------------
 * The price under the buttons is the point of the whole screen. On an hourly
 * service the catalogue price is the rate for a single hour, so "₹499" beside
 * a three-hour booking is a number nobody will be charged - the multiplication
 * has to be on screen, next to the thing that causes it, and it has to move
 * when the choice moves.
 */
export default function HoursPicker({
  service,
  value,
  onChange,
}: {
  service: Service;
  value: number;
  onChange: (hours: number) => void;
}) {
  const choices = hourChoices(service);
  const { min, max } = hourRange(service);

  return (
    <View style={styles.wrap}>
      <View style={styles.heading}>
        <Text variant="label">How long do you need?</Text>
        <Text variant="caption">
          {min === max ? `${min} hr` : `${min}–${max} hrs`}
        </Text>
      </View>

      <View
        style={styles.row}
        accessibilityRole="radiogroup"
        accessibilityLabel="Hours to book"
      >
        {choices.map(hours => {
          const picked = hours === value;
          return (
            <Pressable
              key={hours}
              accessibilityRole="radio"
              accessibilityState={{ selected: picked }}
              accessibilityLabel={`${hours} ${hours === 1 ? 'hour' : 'hours'}`}
              onPress={() => onChange(hours)}
              style={({ pressed }) => [
                styles.choice,
                picked ? styles.choicePicked : null,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text
                variant="labelBold"
                color={picked ? colors.onPrimary : colors.inkBody}
              >
                {hours}
              </Text>
              <Text
                variant="caption"
                color={picked ? colors.inkInverseMuted : colors.inkMuted}
              >
                {hours === 1 ? 'hr' : 'hrs'}
              </Text>
            </Pressable>
          );
        })}
      </View>

      {/*
        The arithmetic, spelled out rather than only totalled.

        "3 × ₹499" beside the total is what makes the rate legible - without it
        a customer who saw ₹499 on the card has to work out for themselves why
        the button now says ₹1,497, and the likeliest conclusion is that the
        app added something they did not ask for.
      */}
      <Text variant="bodySm">
        {value} × ₹{service.price.toLocaleString('en-IN')} ={' '}
        <Text variant="labelBold">
          ₹{(service.price * value).toLocaleString('en-IN')}
        </Text>
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  heading: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  row: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  choice: {
    /*
     * Equal shares of the row. `flexBasis: 0` with `flexGrow: 1` rather than
     * `width: '25%'`, so a service that offers two hours draws two wide
     * buttons instead of two narrow ones against empty space.
     */
    flexGrow: 1,
    flexBasis: 0,
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderRadius: radii.lg,
    ...hairline,
    borderColor: colors.borderStrong,
    backgroundColor: colors.surface,
  },
  choicePicked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  pressed: { opacity: 0.85 },
});
